import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { BoostMark } from '../../components/ui/BoostMark';
import { LinkButton } from '../../components/ui/Button';
import { brand } from '../../config/brand';
import { guessPlatform, megabytes, useDesktopRelease } from '../desktop/useDesktopRelease';
import type { BoostTier, LiveRoom } from '../rooms/api';
import { BroadcastOrigin } from '../rooms/Simulcast';
import { useLiveHosts } from '../rooms/useLiveHosts';
import { platform, type Explanation } from './api';

/**
 * The front door for somebody who has never been here.
 *
 * ## Why this exists again
 *
 * There was a landing page, it was deleted, and the argument for deleting it was
 * good: a site whose proposition is "there is music here" should show the music
 * rather than write a paragraph claiming it. So the dashboard became the home
 * page for everybody.
 *
 * What that missed is that the dashboard answers "what is on" and never answers
 * "what is this". A stranger landing on a wall of tracks by people they have not
 * heard of can tell it is a music site and cannot tell that the records are
 * chosen by a live human being, that anyone can host, that submitting is free,
 * or that a boost cannot buy the host's pick. Those are the things that make it
 * worth an account, and none of them are visible in a grid of artwork.
 *
 * So: this page for a stranger, the dashboard unchanged for everybody signed in.
 * `HomePage` picks. Nobody who already knows what the site is has to read the
 * explanation again.
 *
 * ## Every number on this page comes from the server
 *
 * The weights, the prices and the split are fetched from `/api/explain` rather
 * than typed into the copy. This is the front page - the most expensive place on
 * the site to be wrong - and a hardcoded "80%" outlives the setting it describes
 * by exactly one admin visit. Same argument as the console's queue legend, which
 * is generated from `Weighting` for the same reason.
 *
 * It renders without that data rather than blocking on it: the sections that
 * quote figures are the ones that wait, and the pitch above them does not.
 *
 * ## And every claim is one the code actually makes
 *
 * The mock this was built from promised artists 70% of every boost. The
 * platform pays the *host* 80% of the net and the artist nothing - a boost is a
 * listener moving a track up a host's queue, and what an artist earns from
 * championing other people is a Vibed Boost, which is not money. That is what
 * the page says, because a front page is the last place to describe a product
 * that does not exist.
 */
export function LandingPage() {
  const { items: live } = useLiveHosts();

  const { data: explain } = useQuery({
    queryKey: ['explain'],
    queryFn: () => platform.explain(),
    // The terms of the platform, not a feed. Refetching them on every focus
    // would be three requests an hour to be told the same four constants.
    staleTime: 10 * 60_000,
  });

  return (
    <div className="flex flex-col">
      <Hero live={live} />

      <div className="mx-auto flex w-full max-w-[1240px] flex-col gap-16 px-5 py-14 sm:gap-24 sm:px-10 sm:py-16">
        {/*
          Lifted over the hero's bottom edge rather than sitting below it, so the
          two read as one opening instead of a banner and then a page. The pull
          is the container's own top padding plus about thirty points, which is
          why it differs by breakpoint.
        */}
        <Facts />
        <HowItWorks />
        <Boosts explain={explain} />
        <OnAir live={live} />
        <Desktop />
        <Ending />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Hero                                                                */
/* ------------------------------------------------------------------ */

/**
 * The claim, and one live room beside it.
 *
 * ## The panel is real or it is an explanation
 *
 * The mock put an invented station in the hero - a name, a track, 412 people
 * listening. Shipping that would make the first thing a visitor sees the one
 * thing on the page that is false, and the fake numbers would be there on the
 * quiet Tuesday when the real answer is nobody.
 *
 * So it shows an actual broadcast when there is one, and when there is not it
 * says what a station is instead of pretending. The empty case is the common
 * one early on, which is the argument for writing it properly rather than
 * treating it as a fallback.
 */
function Hero({ live }: { live: LiveRoom[] }) {
  const room = live[0];

  return (
    <section className="relative overflow-hidden border-b border-line">
      {/* Lit from the top left and falling away to the page's own ground, so
          the sections below read as a continuation rather than a change. */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(115% 130% at 16% 0%, color-mix(in srgb, var(--color-accent) 16%, var(--color-app)) 0%, color-mix(in srgb, var(--color-accent) 6%, var(--color-app)) 42%, var(--color-app) 78%)',
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            'linear-gradient(to right, color-mix(in srgb, var(--color-accent) 12%, transparent) 1px, transparent 1px)',
          backgroundSize: '60px 100%',
        }}
      />

      <div className="relative mx-auto grid w-full max-w-[1240px] gap-12 px-5 py-16 sm:px-10 sm:py-20 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:items-center lg:gap-16">
        <div className="flex flex-col items-start gap-7">
          {live.length > 0 ? (
            <span className="flex items-center gap-2.5 rounded-[var(--radius-pill)] border border-hot-edge bg-hot-wash px-4 py-2">
              <span className="h-2 w-2 animate-pulse rounded-full bg-hot-text" aria-hidden />
              <span className="meta-caps text-hot-text">
                {live.length === 1 ? '1 station live right now' : `${live.length} stations live right now`}
              </span>
            </span>
          ) : (
            /* Not a fake live count. The quiet hour is a real state and the
               honest thing to put here is the offer instead. */
            <span className="flex items-center gap-2.5 rounded-[var(--radius-pill)] border border-line bg-raised px-4 py-2">
              <img src={brand.logoMarkMini} alt="" aria-hidden className="h-3.5 w-3.5 rounded-[4px]" />
              <span className="meta-caps text-soft">Free to listen · no account needed</span>
            </span>
          )}

          {/*
            One word in the accent, and it is the word the sentence turns on.
            Colouring the whole line would be decoration; colouring `person` is
            the argument - the difference between this and a recommender is that
            somebody is there.
          */}
          <h1 className="text-pretty text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-[3.75rem]">
            Radio, if every station was a <span className="text-accent">person</span>.
          </h1>

          <p className="max-w-xl text-pretty text-lg leading-relaxed text-soft">
            {brand.wordmark} is live radio run by artists and the people who listen to them. Anyone
            can host a station, anyone can submit a track to one, and what plays next is decided by
            the room and the host, never by an algorithm.
          </p>

          <div className="flex flex-wrap gap-3">
            <LinkButton to="/discover" className="px-7 py-3.5 text-base font-bold">
              Start listening
            </LinkButton>
            <LinkButton to="/register" variant="secondary" className="px-7 py-3.5 text-base">
              Put a track up
            </LinkButton>
          </div>

          {/* What a visitor gets, not what a host earns. The split reads to
              somebody deciding whether to press play as a price. */}
          <span className="font-mono text-[11px] text-faint">
            free to listen, no account needed · every track says how it was made
          </span>
        </div>

        {/*
          One frame: a drawn station, and under it either the broadcast that is
          actually on or a description of what one is.

          The illustration replaced a screenshot of the app. A capture carries
          the header, the chat rail and the dock around the thing it is meant to
          show, so it says "here is some software" where a drawing says "here is
          a room" - and the drawing is the same shape whatever the product looks
          like next month.
        */}
        <div className="flex flex-col overflow-hidden rounded-[20px] border border-line bg-surface shadow-[0_60px_120px_-60px_color-mix(in_srgb,var(--color-accent)_35%,transparent)]">
          <div className="relative aspect-[16/9] bg-app">
            <img
              src="/brand/stage-hero.svg"
              alt="A live station drawn as a stage: a screen, speaker stacks and a crowd"
              className="absolute inset-0 h-full w-full object-cover"
              loading="eager"
            />

            <span className="absolute left-3.5 top-3.5 flex items-center gap-2 rounded-[var(--radius-pill)] border border-hot-edge bg-app/80 px-3 py-1.5 backdrop-blur-[4px]">
              <span
                className={`h-[7px] w-[7px] rounded-full bg-hot-text ${room ? 'animate-pulse' : ''}`}
                aria-hidden
              />
              {/*
                The label follows the truth below it. Saying "live" over a
                drawing while nobody is broadcasting would make the first thing
                a visitor reads the one thing on the page that is false.
              */}
              <span className="meta-caps text-hot-text">
                {room ? 'on air right now' : 'this is a station'}
              </span>
            </span>
          </div>

          <div className="border-t border-line">
            {room ? <LiveCard room={room} /> : <StationExplainer />}
          </div>
        </div>
      </div>
    </section>
  );
}

function LiveCard({ room }: { room: LiveRoom }) {
  return (
    <Link
      to={`/${room.username}`}
      data-hero-room={room.username}
      className="flex flex-col gap-5 p-6 transition-colors hover:bg-raised"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-hot-text" aria-hidden />
          <span className="meta-caps truncate text-hot-text">
            live · {room.name ?? room.host.display_name}
          </span>
        </span>
        <span className="shrink-0 font-mono text-[10px] text-faint">
          {room.listener_count} listening
        </span>
      </div>

      <div className="flex items-center gap-4">
        {room.now_playing?.artwork_small ? (
          <img
            src={room.now_playing.artwork_small}
            alt=""
            className="h-20 w-20 shrink-0 rounded-[var(--radius-chip)] object-cover"
          />
        ) : (
          <div
            aria-hidden
            className="h-20 w-20 shrink-0 rounded-[var(--radius-chip)]"
            style={{
              backgroundImage:
                'repeating-linear-gradient(45deg,var(--color-blank) 0 8px,var(--color-blank-alt) 8px 16px)',
            }}
          />
        )}

        <div className="flex min-w-0 flex-col gap-1.5">
          {/* "Between tracks" is a real state - a host mid-crate dig has not
              stopped broadcasting - so it is said rather than left blank. */}
          <span className="truncate text-lg font-bold tracking-tight text-ink">
            {room.now_playing ? room.now_playing.title : 'Between tracks'}
          </span>
          {room.now_playing && (
            <span className="truncate font-mono text-[11px] text-soft">
              {room.now_playing.artist}
            </span>
          )}
          <span className="flex items-center gap-2 font-mono text-[10px] text-faint">
            hosted by @{room.host.username}
            <BroadcastOrigin simulcast={room.simulcast} size="sm" />
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-line pt-4 font-mono text-[10px]">
        <span className={room.submissions === 'open' ? 'text-fresh' : 'text-faint'}>
          {room.submissions === 'open'
            ? 'taking submissions'
            : room.submissions === 'followers'
              ? 'submissions from followers'
              : 'submissions closed'}
        </span>
        <span className="text-accent">drop in →</span>
      </div>
    </Link>
  );
}

/** What a station is, for the hours when none of them are on. */
function StationExplainer() {
  return (
    <div className="flex flex-col gap-3.5 p-6">
      <span className="meta-caps text-faint">What a station is</span>

      <p className="text-pretty text-sm leading-relaxed text-soft">
        One person, live, playing records to whoever turns up. They queue their own crate and take
        submissions from the room, so the way onto a station is to send the host a track, not to
        be picked up by a recommender.
      </p>

      {/*
        A row rather than a stacked list, and each dot the colour of the thing
        it is about: the room is accent, what is open to read is fresh, the way
        in is hot. The same three colours carry the four claims below and the
        three steps under those, so the page has a palette rather than a border.
      */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[11px] text-faint">
        <Bullet tone="bg-accent">live, they come and go</Bullet>
        <Bullet tone="bg-fresh">chat and queue open to read</Bullet>
        <Bullet tone="bg-hot-text">anyone can apply to host</Bullet>
      </div>

      <Link to="/discover" className="font-mono text-xs text-accent hover:underline">
        Look through the music instead →
      </Link>
    </div>
  );
}

function Bullet({ children, tone }: { children: ReactNode; tone: string }) {
  return (
    <span className="flex items-center gap-[7px]">
      <span className={`h-[5px] w-[5px] shrink-0 rounded-full ${tone}`} aria-hidden />
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Facts                                                               */
/* ------------------------------------------------------------------ */

/**
 * Four things that are true, rather than four things that are big.
 *
 * The mock had a counter strip - 48,000 artists, $412,000 paid out, 212 stations
 * live. Invented, and the shape of the thing is the problem rather than the
 * particular lies in it: a number that is meant to impress argues *against* you
 * on the day it is small, which on a young platform is every day. These four
 * are as true in the first month as in the fiftieth.
 */
/**
 * The four claims, and why the money is no longer one of them.
 *
 * The split used to sit in the third slot. It moved to `ForHostsPage`, where
 * the audience is people deciding whether to run a room - which is the only
 * audience it was ever for. A stranger deciding whether to listen does not need
 * to know how a host is paid, and the slot is worth more said to them.
 *
 * What took it is the thing nobody else says out loud. Every other platform is
 * either quietly full of AI music or loudly against it; this one welcomes it and
 * labels it, and that pair is the position. See `provenance.tsx`.
 */
function Facts() {
  return (
    <section className="relative z-[1] -mt-10 grid grid-cols-[repeat(auto-fit,minmax(230px,1fr))] gap-4">
      <Fact title="Free to listen" edge="border-t-ink" ink="text-ink">
        No account, no app, no trial. Rooms, chat and queues are open to read.
      </Fact>
      <Fact title="No algorithm" edge="border-t-accent" ink="text-accent">
        A person picks every record. What plays next is the host's call and the room's.
      </Fact>
      <Fact title="AI is welcome here" edge="border-t-fresh" ink="text-fresh">
        Made with it, made without it, or somewhere in between. Every track says which, in
        the artist&rsquo;s own words.
      </Fact>
      <Fact title="No gatekeeper" edge="border-t-hot-text" ink="text-hot-text">
        No distributor, no release date, no approval queue. Upload it and it is up.
      </Fact>
    </section>
  );
}

/**
 * One claim, with a colour of its own.
 *
 * They were four panes of one divided slab, which read as a table of contents
 * rather than as four things worth knowing. Separated, each with a lit top edge
 * and a heading in the same colour, they read as four claims - and the page
 * gets the palette the brand mark has and the rest of the site mostly does not.
 */
function Fact({
  title,
  edge,
  ink,
  children,
}: {
  title: string;
  edge: string;
  ink: string;
  children: ReactNode;
}) {
  return (
    <div
      className={`flex flex-col gap-2.5 rounded-[14px] border border-line border-t-2 bg-surface px-6 py-6 ${edge}`}
    >
      <span className={`text-[19px] font-bold tracking-tight ${ink}`}>{title}</span>
      <span className="text-pretty text-[13px] leading-relaxed text-soft">{children}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* How it works                                                        */
/* ------------------------------------------------------------------ */

function HowItWorks() {
  return (
    <section className="flex flex-col gap-9">
      <Heading eyebrow="How it works">
        Three steps between a track on your hard drive and a room full of people hearing it.
      </Heading>

      <div className="grid gap-4 lg:grid-cols-3">
        <Step
          n="1"
          tone="accent"
          title="Put a track up"
          body="No distributor, no release date, nobody to ask. Upload it and it is on your profile, playable by anyone with the link, in about a minute."
        />
        <Step
          n="2"
          tone="hot"
          title="Send it to a room that's live"
          body="Free, and to any host whose door is open. Your track joins their queue and the whole room hears it when it comes up, including the host, who can follow you on the spot."
        />
        <Step
          n="3"
          tone="fresh"
          title="The room decides what's next"
          body="Every submission is in the running. Weight moves a track up the order, being new to a room helps, and the host keeps one pick that nothing can outbid."
        />
      </div>
    </section>
  );
}

/** Each tier drawn as the kind of thing it is. See the list for the reasoning. */
const TIER_TONE: Partial<Record<BoostTier, string>> = {
  free: 'border-line bg-app',
  basic: 'border-line-strong bg-app',
  priority: 'border-hot-edge bg-hot-wash',
  earned: 'border-fresh-edge bg-fresh-wash',
  pick: 'border-line-strong bg-app',
};

const TIER_DOT: Partial<Record<BoostTier, string>> = {
  free: 'bg-faint',
  basic: 'bg-accent',
  priority: 'bg-hot-text',
  earned: 'bg-fresh',
  pick: 'bg-ink',
};

const STEP_TONE = {
  accent: { border: 'border-line-strong', text: 'text-accent', bar: 'bg-accent' },
  hot: { border: 'border-hot-edge', text: 'text-hot-text', bar: 'bg-hot-text' },
  fresh: { border: 'border-fresh-edge', text: 'text-fresh', bar: 'bg-fresh' },
} as const;

function Step({
  n,
  tone,
  title,
  body,
}: {
  n: string;
  tone: keyof typeof STEP_TONE;
  title: string;
  body: string;
}) {
  const t = STEP_TONE[tone];

  return (
    <div
      className={`relative flex flex-col gap-3.5 overflow-hidden rounded-[var(--radius-card)] border ${t.border} bg-surface p-7`}
    >
      {/*
        The number, set large and faint in the step's own colour rather than as
        a caption above the title. It gives the card something to look at from
        across the page, which is what the three of them lacked: an ordering you
        could see before you read any of it.
      */}
      <span
        aria-hidden
        className={`pointer-events-none absolute right-5 top-1 select-none text-[5.5rem] font-bold leading-none tracking-tight opacity-[0.14] ${t.text}`}
      >
        {n}
      </span>

      {/* The site's own motif at small size: three bars, one taller. It is
          decoration, so it is hidden rather than described. */}
      <span aria-hidden className="flex h-6 items-end gap-1">
        <span className={`w-1.5 rounded-t-[var(--radius-pill)] ${t.bar} opacity-50`} style={{ height: '45%' }} />
        <span className={`w-1.5 rounded-t-[var(--radius-pill)] ${t.bar}`} style={{ height: '100%' }} />
        <span className={`w-1.5 rounded-t-[var(--radius-pill)] ${t.bar} opacity-70`} style={{ height: '65%' }} />
      </span>

      <span className="relative text-xl font-bold leading-tight tracking-tight text-ink">
        {title}
      </span>
      <p className="text-pretty text-sm leading-relaxed text-soft">{body}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Boosts                                                              */
/* ------------------------------------------------------------------ */

/**
 * The part that has to be exactly right.
 *
 * It is the only section describing what somebody's money does, so every figure
 * in it - the multipliers, the two prices, the bonuses, the tie-break, the split
 * - is rendered from `/api/explain` and none of it is written in this file. If
 * the response has not arrived the section is not drawn, which is the correct
 * failure: a pricing table missing for a second is a smaller problem than a
 * pricing table that is confidently out of date.
 */
function Boosts({ explain }: { explain?: Explanation }) {
  if (!explain) return null;

  const price: Partial<Record<BoostTier, number>> = {
    basic: explain.boosts.basic_cents,
    priority: explain.boosts.priority_cents,
  };

  const NOTE: Record<BoostTier, string> = {
    free: 'always in the running, and the queue is never pay-only',
    basic: 'ahead of everything unboosted',
    priority: 'the most money can do here',
    earned: 'earned, never sold, and it outranks both paid tiers',
    pick: "the host's own call, at any price",
  };

  return (
    <section className="grid gap-4 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
      <div className="flex flex-col gap-6 rounded-[var(--radius-card)] border border-line bg-surface p-7 sm:p-9">
        <Heading eyebrow="Boosts, explained" size="sm">
          Money moves you up the queue. It never buys the room.
        </Heading>

        <p className="max-w-xl text-pretty text-[15px] leading-relaxed text-soft">
          Every submission gets in line for free. A boost multiplies a track's weight so it comes up
          sooner. It does not jump the queue, and it cannot outrank a host's own pick or a boost
          somebody earned.
        </p>

        {/*
          A row apiece, each carrying the colour of what it is: free is nothing
          in particular, money is accent then hot as it gets more expensive, and
          earned is fresh because it is the one that cannot be bought. The list
          was four lines of text in a column and the ordering it describes was
          the one thing you could not see in it.
        */}
        <ul className="flex flex-col gap-2">
          {explain.weighting.tiers.map((tier) => (
            <li
              key={tier.tier}
              className={`flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-[var(--radius-control)] border px-4 py-3 ${TIER_TONE[tier.tier] ?? 'border-line bg-app'}`}
            >
              <span className="flex items-center gap-2.5 text-[15px] font-bold text-ink sm:min-w-[9.5rem]">
                <span
                  aria-hidden
                  className={`h-[7px] w-[7px] shrink-0 rounded-full ${TIER_DOT[tier.tier] ?? 'bg-faint'}`}
                />
                {tier.label}
              </span>
              <span className="font-mono text-xs text-accent sm:min-w-[4rem]">
                {tier.weight === null ? 'pinned' : `×${tier.weight}`}
              </span>
              {/*
                Its own line on a phone, beside the weight from `sm` up. Three
                columns in 330 points leaves this one about eighty, and a note
                wrapping at four characters a line is not a note.
              */}
              <span className="w-full font-mono text-[11px] text-faint sm:w-auto sm:min-w-0 sm:flex-1">
                {price[tier.tier] !== undefined && `$${(price[tier.tier]! / 100).toFixed(2)} · `}
                {NOTE[tier.tier]}
              </span>
            </li>
          ))}
        </ul>

        {/* The two thumbs on the scale, said out loud. A rule somebody works
            out by watching an order they did not expect is a rule they assume
            was hidden on purpose. */}
        <div className="flex flex-col gap-2 border-t border-line pt-5">
          {explain.weighting.bonuses.map((bonus) => (
            <span key={bonus.label} className="font-mono text-[11px] text-soft">
              <span className="text-fresh">+{bonus.weight}</span> {bonus.label}
            </span>
          ))}
          <span className="font-mono text-[11px] text-faint">
            ties broken by {explain.weighting.tie_break}
          </span>
        </div>
      </div>

      {/*
        The earned boost, given a column of its own.
        
        It was two lines inside the card on the left, which is where the revenue
        split used to have a whole panel - and of the two, this is the one aimed
        at the person reading. "Do I get paid when somebody boosts my track" is
        the obvious next question and the answer is no, so it is answered here
        rather than left for payout day.
      */}
      <div className="flex flex-col gap-5 rounded-[var(--radius-card)] border border-fresh-edge bg-fresh-wash p-7 sm:p-9">
        <span className="meta-caps text-fresh">The boost nobody can buy</span>

        <span className="flex items-center gap-3.5">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-chip)] border border-fresh-edge bg-app">
            <BoostMark className="h-5 w-5 text-fresh" />
          </span>
          <span className="text-[22px] font-bold tracking-tight text-ink">
            {brand.name} Boosts
          </span>
        </span>

        <p className="text-pretty text-sm leading-relaxed text-soft">
          Artists earn them, rather than a cut, by sending other people&rsquo;s tracks to hosts who
          play them. They are the one boost nobody can buy, and they outrank both paid tiers.
        </p>

        <div className="flex flex-col gap-1.5 border-t border-fresh-edge pt-5 font-mono text-[11px] text-faint">
          <span>earned · never sold · outranks money</span>
          <span>rewards finding music, not posting it</span>
          <span>your own tracks never count</span>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* On air                                                              */
/* ------------------------------------------------------------------ */

function OnAir({ live }: { live: LiveRoom[] }) {
  return (
    <section className="flex flex-col gap-7">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <Heading eyebrow="On air right now" size="sm" tone="hot">
          {live.length > 0 ? 'Drop into a station.' : 'Nobody is on air this minute.'}
        </Heading>
        <span className="font-mono text-[11px] text-faint">no account needed to listen</span>
      </div>

      {live.length === 0 ? (
        /*
          A dashed frame, because the empty hour is a waiting state rather than
          a thing that is broken. It was a solid card with a paragraph and a
          quiet link, which looked like a section that had failed to load.
        */
        <div className="flex flex-col items-start gap-6 rounded-[var(--radius-card)] border border-dashed border-line-strong p-8 sm:flex-row sm:items-center sm:gap-8">
          {/* The motif again, faint: the room is there, nobody is in it. */}
          <span aria-hidden className="flex h-12 items-end gap-1.5 opacity-25">
            {[40, 70, 100, 55, 30].map((h, i) => (
              <span
                key={i}
                className="w-2 rounded-t-[var(--radius-pill)] bg-soft"
                style={{ height: `${h}%` }}
              />
            ))}
          </span>

          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <span className="text-[15px] font-bold tracking-tight text-ink">
              Stations are people, so they keep human hours.
            </span>
            <p className="text-pretty text-sm leading-relaxed text-soft">
              When one opens it appears here and in the header, wherever you are on the site. The
              way this stops being empty is somebody reading it and going live.
            </p>
          </div>

          <Link
            to="/host"
            className="shrink-0 rounded-[var(--radius-pill)] border border-hot-edge bg-hot-wash px-5 py-2.5 text-sm font-bold text-hot-text transition-opacity hover:opacity-85"
          >
            Apply to host →
          </Link>
        </div>
      ) : (
        /*
          The column count follows the rooms rather than the other way round.
          
          It was fixed at four, so a single broadcast sat in the top left of a
          row that was three quarters empty black - and the one section whose
          job is to show the place has people in it was the section that made it
          look abandoned. A grid sized to what exists never has a hole in it.
        */
        <div
          className={`grid gap-4 ${
            live.length === 1
              ? 'sm:grid-cols-1'
              : live.length === 2
                ? 'sm:grid-cols-2'
                : live.length === 3
                  ? 'sm:grid-cols-2 lg:grid-cols-3'
                  : 'sm:grid-cols-2 lg:grid-cols-4'
          }`}
          data-landing-rooms
        >
          {live.slice(0, 4).map((room) => (
            <RoomCard key={room.id} room={room} wide={live.length === 1} />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * A live room, at one of two sizes.
 *
 * `wide` is for the night when exactly one station is on, which early on is
 * most nights. The narrow card is built to sit in a row of four and looks
 * stranded on its own, so the single case gets a card that fills the width and
 * uses the artwork at a size worth looking at - the same information, arranged
 * like it is the thing on the page rather than the first of several.
 *
 * The artwork also does the colouring. This is a music platform whose front
 * page had almost no colour in it that was not a button, and the record already
 * playing is the most honest place to get some.
 */
function RoomCard({ room, wide = false }: { room: LiveRoom; wide?: boolean }) {
  if (wide) {
    return (
      <Link
        to={`/${room.username}`}
        className="group relative isolate flex overflow-hidden rounded-[var(--radius-card)] border border-hot-edge bg-hot-wash transition-colors hover:border-hot-text"
      >
        {/* The record, blurred, as the light in the card. Hidden from the
            accessibility tree: it is the same image as the one below it. */}
        {room.now_playing?.artwork_small && (
          <div
            aria-hidden
            className="absolute inset-0 -z-10 scale-110 bg-cover bg-center opacity-25 blur-2xl"
            style={{ backgroundImage: `url(${room.now_playing.artwork_small})` }}
          />
        )}

        <div className="flex min-w-0 flex-1 flex-col gap-5 p-6 sm:flex-row sm:items-center sm:gap-7 sm:p-8">
          {room.now_playing?.artwork_small ? (
            <img
              src={room.now_playing.artwork_small}
              alt=""
              className="h-28 w-28 shrink-0 rounded-[var(--radius-chip)] object-cover shadow-2xl sm:h-32 sm:w-32"
            />
          ) : (
            <div
              aria-hidden
              className="h-28 w-28 shrink-0 rounded-[var(--radius-chip)] sm:h-32 sm:w-32"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(45deg,var(--color-blank) 0 6px,var(--color-blank-alt) 6px 12px)',
              }}
            />
          )}

          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <span className="flex items-center gap-2.5">
              <span className="h-2 w-2 animate-pulse rounded-full bg-hot-text" aria-hidden />
              <span className="meta-caps text-hot-text">
                live · {room.listener_count} listening
              </span>
            </span>

            <span className="truncate text-2xl font-bold tracking-tight text-ink sm:text-3xl">
              {room.name ?? room.host.display_name}
            </span>

            <span className="flex min-w-0 items-center gap-1.5 font-mono text-xs text-soft">
              <span className="truncate">
                @{room.host.username}
                {room.genre !== null && ` · ${room.genre}`}
              </span>
              <BroadcastOrigin simulcast={room.simulcast} size="sm" />
            </span>

            <span className="truncate font-mono text-[11px] text-faint">
              {room.now_playing ? `now: ${room.now_playing.title}` : 'between tracks'}
            </span>
          </div>

          <span className="shrink-0 font-mono text-[11px] text-hot-text transition-transform group-hover:translate-x-0.5">
            drop in →
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/${room.username}`}
      className="flex flex-col gap-4 rounded-[var(--radius-card)] border border-hot-edge bg-hot-wash p-5 transition-colors hover:border-hot-text"
    >
      <div className="flex items-center justify-between gap-2">
        {room.now_playing?.artwork_small ? (
          <img
            src={room.now_playing.artwork_small}
            alt=""
            className="h-11 w-11 rounded-[var(--radius-chip)] object-cover"
          />
        ) : (
          <div
            aria-hidden
            className="h-11 w-11 rounded-[var(--radius-chip)]"
            style={{
              backgroundImage:
                'repeating-linear-gradient(45deg,var(--color-blank) 0 6px,var(--color-blank-alt) 6px 12px)',
            }}
          />
        )}
        <span className="font-mono text-[10px] text-hot-text">{room.listener_count} listening</span>
      </div>

      <div className="flex min-w-0 flex-col gap-1">
        <span className="truncate text-[17px] font-bold tracking-tight text-ink">
          {room.name ?? room.host.display_name}
        </span>
        <span className="flex min-w-0 items-center gap-1.5 font-mono text-[11px] text-soft">
          <span className="truncate">
            @{room.host.username}
            {room.genre !== null && ` · ${room.genre}`}
          </span>
          {/* The card is one big link, so this never becomes a second one. */}
          <BroadcastOrigin simulcast={room.simulcast} size="sm" />
        </span>
      </div>

      <span className="truncate font-mono text-[10px] text-faint">
        {room.now_playing ? `now: ${room.now_playing.title}` : 'between tracks'}
      </span>
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Desktop app                                                         */
/* ------------------------------------------------------------------ */

/**
 * The app, shown rather than described.
 *
 * ## Why it is on the front page and not only in the menu
 *
 * The footer link and the account-menu row are both for people who already know
 * what this site is. A stranger deciding whether to make an account is the one
 * person who has never seen the product at all, and three screenshots answer
 * "what is this" faster than the two thousand words above them.
 *
 * ## It shows the app, not a mock of it
 *
 * Real captures of a real build - the venue, the dashboard and the host console
 * - for the same reason the hero shows an actual broadcast and says so when
 * there is not one. A rendered mock on a front page is the one image on the
 * site that can go stale without anybody noticing.
 *
 * ## Absent unless there is something to install
 *
 * `useDesktopRelease` answers null before the first release is published and
 * inside the desktop app itself, and a build has to exist for the platform
 * being read from - handing a Mac an `.exe` is worse than saying nothing, which
 * is `DesktopDownload`'s rule and this follows it. Which means: no release, no
 * section, and no screenshots of software nobody can get.
 */
function Desktop() {
  const release = useDesktopRelease();
  const platform = guessPlatform();

  /*
   * Their platform's build, or the only one there is when the browser will not
   * say - Safari and Firefox both withhold the hint. Offering the single
   * published build to somebody unidentified is a better guess than nothing,
   * and the label on the button says which it is.
   */
  const build = release
    ? (platform ? release.platforms[platform] : Object.values(release.platforms)[0])
    : undefined;

  if (!release || !build) return null;

  const size = megabytes(build.size);

  return (
    <section className="flex flex-col gap-7" data-landing-desktop>
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="flex max-w-2xl flex-col gap-3">
          <span className="meta-caps text-faint">Desktop app</span>
          <h2 className="text-pretty text-2xl font-bold leading-[1.1] tracking-tight text-ink sm:text-3xl">
            The whole thing, in a window of its own.
          </h2>
          <p className="text-pretty text-[15px] leading-relaxed text-soft">
            The same site with no tab to lose. Browsers throttle animation in a window you are not
            looking at, which is fine for a page and not fine for a broadcast, so this build turns
            that off: a host mixing in another window keeps sending a picture, and a room keeps
            moving while you work. It updates itself before it opens.
          </p>
        </div>

        <div className="flex flex-col items-start gap-2">
          {/*
            A plain anchor with `download`, not a router `Link`: this leaves the
            application for a file, which the SPA router would try to resolve as
            a route and answer with the not-found page. Same reasoning, and the
            same trap, as `DesktopDownload`.
          */}
          <a
            href={build.url}
            download
            data-landing-desktop-download
            className="inline-flex items-center justify-center gap-2 rounded-[var(--radius-pill)] border border-transparent bg-accent px-7 py-3.5 text-base font-bold text-accent-ink transition-colors hover:bg-accent-hover"
          >
            Download for {build.label}
          </a>

          <span className="font-mono text-[11px] text-faint">
            {release.version !== null && `v${release.version}`}
            {release.version !== null && size !== null && ' · '}
            {size}
          </span>
        </div>
      </div>

      {/*
        The venue first and large, because it is the thing nothing else on the
        internet looks like, then the two screens somebody would actually live
        in. Captured at 1600px and drawn at whatever the column is; `width` and
        `height` are on them so the page does not jump as they arrive.

        `/screens` and deliberately not `/desktop`, which is where these lived
        for exactly one deploy. Production aliases `/desktop` to the directory
        the installers are published into - it is the other half of
        `DESKTOP_DOWNLOAD_URL` - so an upload of the same name is shadowed by
        the alias and every image 404s in production and nowhere else.
      */}
      <div className="flex flex-col gap-4">
        <Shot
          src="/screens/stage.webp"
          caption="A live room, drawn as a venue: the host behind the decks, the people in the room in front of them."
          alt="The desktop app showing a live room as a drawn stage, with artwork on the screen behind the host and chat down the side."
          eager
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Shot
            src="/screens/dashboard.webp"
            caption="Your own front page: what is new, who is on air, and what your artists are saying."
            alt="The desktop app showing the dashboard: favorites, a featured track, live hosts and new music."
          />
          <Shot
            src="/screens/console.webp"
            caption="The host console: the deck, the queue and the room, on one screen."
            alt="The desktop app showing the host console: the studio preview, the submission queue and room chat."
          />
        </div>
      </div>
    </section>
  );
}

/**
 * One screenshot, framed.
 *
 * `loading="lazy"` on everything below the first, because this section is at
 * the bottom of a long page and most visitors never reach it - three images
 * fetched eagerly would be paid for by every reader who does not.
 */
function Shot({
  src,
  alt,
  caption,
  eager = false,
}: {
  src: string;
  alt: string;
  caption: string;
  eager?: boolean;
}) {
  return (
    <figure className="flex flex-col gap-2.5">
      <div className="overflow-hidden rounded-[var(--radius-card)] border border-line bg-void shadow-[0_24px_60px_-40px_rgba(0,0,0,0.8)]">
        <img
          src={src}
          alt={alt}
          width={1600}
          height={860}
          loading={eager ? undefined : 'lazy'}
          decoding="async"
          className="block h-auto w-full"
        />
      </div>
      <figcaption className="text-pretty font-mono text-[11px] leading-relaxed text-faint">
        {caption}
      </figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/* Ending                                                              */
/* ------------------------------------------------------------------ */

function Ending() {
  return (
    /*
      Lit from the left, so the last thing on the page is not the same flat
      card as the eight above it. The gradient is the accent at low strength
      over the ordinary surface, which is the hero's own treatment brought back
      to close the page where it opened.
    */
    <section
      className="relative flex flex-wrap items-center justify-between gap-8 overflow-hidden rounded-[var(--radius-card)] border border-line-strong p-8 sm:p-12"
      style={{
        background:
          'linear-gradient(105deg, color-mix(in srgb, var(--color-accent) 14%, var(--color-surface)) 0%, var(--color-surface) 55%)',
      }}
    >
      <div className="flex max-w-xl flex-col gap-3">
        <h2 className="text-pretty text-3xl font-bold leading-tight tracking-tight text-ink">
          The people who would like your record are in somebody's room tonight.
        </h2>
        <p className="text-pretty text-[15px] leading-relaxed text-soft">
          Put a track up, send it to a station that is on air, and find out who is listening.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <LinkButton to="/register" className="px-7 py-3.5 text-base font-bold">
          Create an account
        </LinkButton>
        <LinkButton to="/discover" variant="secondary" className="px-7 py-3.5 text-base">
          Browse the music
        </LinkButton>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */

function Heading({
  eyebrow,
  size = 'lg',
  tone = 'faint',
  children,
}: {
  eyebrow: string;
  size?: 'sm' | 'lg';
  tone?: 'faint' | 'hot';
  children: ReactNode;
}) {
  return (
    <div className="flex max-w-2xl flex-col gap-3">
      <span className={`meta-caps ${tone === 'hot' ? 'text-hot-text' : 'text-faint'}`}>
        {eyebrow}
      </span>
      <h2
        className={`text-pretty font-bold leading-[1.1] tracking-tight text-ink ${
          size === 'lg' ? 'text-3xl sm:text-4xl' : 'text-2xl sm:text-3xl'
        }`}
      >
        {children}
      </h2>
    </div>
  );
}
